import { atomFamily } from 'recoil';
import { WorklistFiltersType, FilterVariantType } from '../WorklistFilters/types';
import { marks } from './marks';

export const conflictingEndMark: Record<string, string> = {
  [marks.worklist.crud.abandon]: marks.worklist.crud.submit,
  [marks.worklist.crud.save]: marks.worklist.crud.abandon,
  [marks.worklist.crud.submit]: marks.worklist.crud.abandon,
};

export const prevEndMark: Record<string, string> = {
  [marks.worklist.load.data_ready]: marks.worklist.load.request_fired,
  [marks.worklist.load.paint]: marks.worklist.load.data_ready,
  [marks.worklist.pagination.data_ready]: marks.worklist.pagination.request_fired,
  [marks.worklist.pagination.paint]: marks.worklist.pagination.data_ready,
  [marks.worklist.columns.resize_save_end]: marks.worklist.columns.resize_end,
};

// TTL thresholds in milliseconds, after which a measurement is considered stale and will be ignored
// These are generous thresholds, as a safeguard against cases where the end mark is never created
// (e.g. user navigates away, or an error occurs)
export const ttlThresholds: Record<string, number> = {
  [marks.worklist.load.request_fired]: 10_000,
  [marks.worklist.load.data_ready]: 45_000,
  [marks.worklist.load.paint]: 60_000,
  [marks.worklist.pagination.request_fired]: 10_000,
  [marks.worklist.pagination.data_ready]: 45_000,
  [marks.worklist.pagination.paint]: 60_000,
  [marks.worklist.columns.pin_save]: 10_000,
  [marks.worklist.columns.pin_paint]: 15_000,
  [marks.worklist.columns.reorder_save]: 10_000,
  [marks.worklist.columns.reorder_paint]: 15_000,
  [marks.worklist.drawer.open_paint]: 5_000,
};

export type MeasureEntry = {
  metricName: string;
  startMarkName: string;
  endMarkName: string; // may be shared across different measures/metrics
};

export type FilterPresenceDetails = {
  filterCountBucket?: '0' | '1-2' | '3-5' | '6+';
  filterKeys: Array<string>;
};

export const WorklistLoadTrigger = Object.freeze({
  InitialLoad: 'initial_load',
  Search: 'search',
  SortChange: 'sort_change',
  FilterUpdate: 'filter_update',
  FilterReset: 'filter_reset',
  WorklistSwitch: 'worklist_switch',
  WorklistCombine: 'worklist_combine',
});

export type WorklistLoadTriggerType =
  (typeof WorklistLoadTrigger)[keyof typeof WorklistLoadTrigger];

export const WorklistTypeDetail = Object.freeze({
  Public: 'public',
  Private: 'private',
  Canned: 'canned',
  Legacy: 'legacy',
});

export type WorklistTypeDetailType = (typeof WorklistTypeDetail)[keyof typeof WorklistTypeDetail];

export const WorklistMode = Object.freeze({
  Single: 'single',
  Combined: 'combined',
});

export type WorklistModeType = (typeof WorklistMode)[keyof typeof WorklistMode];

export type RowsBucketType =
  | '0'
  | '1-50'
  | '51-200'
  | '201-500'
  | '500-1000'
  | '1000-5000'
  | '5000+';

export const ResetFiltersEntrypoint = Object.freeze({
  Footer: 'footer',
  SaveWorklistButton: 'save_worklist_button',
});

export type ResetFiltersEntrypointType =
  (typeof ResetFiltersEntrypoint)[keyof typeof ResetFiltersEntrypoint];

export const WorklistCombineAction = Object.freeze({
  Combine: 'combine',
  Remove: 'remove',
});

export type WorklistCombineActionType =
  (typeof WorklistCombineAction)[keyof typeof WorklistCombineAction];

export const WorklistCombineEntrypoint = Object.freeze({
  LeftDrawer: 'left_drawer',
  Chip: 'chip',
});

export type WorklistCombineEntrypointType =
  (typeof WorklistCombineEntrypoint)[keyof typeof WorklistCombineEntrypoint];

export const WorklistDialogMode = Object.freeze({
  Create: 'create',
  Clone: 'clone',
  Edit: 'edit',
});

export type WorklistDialogModeType = (typeof WorklistDialogMode)[keyof typeof WorklistDialogMode];

export const WorklistDialogEntrypoint = Object.freeze({
  LeftDrawer: 'left_drawer',
  SaveWorklistButton: 'save_worklist_button',
  CreateButton: 'create_button',
});

export type WorklistDialogEntrypointType =
  (typeof WorklistDialogEntrypoint)[keyof typeof WorklistDialogEntrypoint];

export const WorklistDialogExit = Object.freeze({
  Cancel: 'cancel',
  Close: 'close',
  Esc: 'esc',
});

export type WorklistDialogExitType = (typeof WorklistDialogExit)[keyof typeof WorklistDialogExit];

export const WorklistMutationResult = Object.freeze({
  Success: 'success',
  Error: 'error',
});

export type WorklistMutationResultType =
  (typeof WorklistMutationResult)[keyof typeof WorklistMutationResult];

export const WorklistColumnAction = Object.freeze({
  Grow: 'grow',
  Shrink: 'shrink',
  Hide: 'hide',
  Show: 'show',
  Reset: 'reset',
});

export type WorklistColumnActionType =
  (typeof WorklistColumnAction)[keyof typeof WorklistColumnAction];

/**
 * These are optional details that can be sent along with a measurement. It is
 * generic in nature to be used for all measurements, by having all keys optional.
 * - trigger: what caused the worklist to load
 * - rowsBucket: how many rows were present in the worklist when the measurement was taken
 * - filterCountBucket: how many filters were active when the measurement was taken
 * - worklistMode: whether the worklist was a single worklist or a combined one
 * - worklistTypes: list of worklist types: public, private, legacy, or canned
 * - loadID: a unique ID for this particular load, to link related events together
 * - filterVariant: corresponding filter variant (like, multiselect or text)
 * - filterKey: corresponding filter key (like examStatus, modality, etc)
 * - resetFilterEntrypoint: which UI entrypoint was used to reset filters, if applicable
 * - changedSorting: list of sorting changes, in the form of ["column1:asc", "column2:removed"]
 * - sorting: current sorting state, in the form of ["column1:asc", "column2:desc"]
 * - combineAction: whether the user combined or removed a worklist
 * - combineEntrypoint: which UI entrypoint was used to combine/remove a worklist
 * - startIndex: for pagination, the start index of the newly loaded rows
 * - stopIndex: for pagination, the end index of the newly loaded rows
 * - worklistDialogMode: whether the user was creating, cloning or editing a custom worklist
 * - worklistDialogEntrypoint: which UI entrypoint was used to open the custom worklist dialog
 * - worklistDialogExit: how the user exited the custom worklist dialog
 * - addedFilterKeys: list of filter keys that were added/changed in the custom worklist dialog
 * - addedFilterCountBucket: how many filters were added/changed in the custom worklist dialog
 * - result: whether the worklist mutation was a success or error
 * - column: a column on the worklist
 * - columnAction: whether the user grew, shrank, hid or showed a column
 * - tableVersion: which table version ('v1' or 'v2') produced this measurement
 * - startedAt: performance.now() timestamp at start, used for TTL enforcement
 */
export type MeasureDetail = {
  trigger?: WorklistLoadTriggerType;
  rowsBucket?: RowsBucketType;
  startingRowsBucket?: RowsBucketType;
  filterCountBucket?: '0' | '1-2' | '3-5' | '6+';
  worklistMode?: WorklistModeType;
  worklistTypes?: Array<WorklistTypeDetailType>;
  loadID?: string;
  filterVariant?: FilterVariantType;
  filterKey?: WorklistFiltersType;
  changedSorting?: string[];
  sorting?: string[];
  resetFilterEntrypoint?: ResetFiltersEntrypointType;
  combineAction?: WorklistCombineActionType;
  combineEntrypoint?: WorklistCombineEntrypointType;
  startIndex?: number;
  stopIndex?: number;
  worklistDialogMode?: WorklistDialogModeType;
  worklistDialogEntrypoint?: WorklistDialogEntrypointType;
  worklistDialogExit?: WorklistDialogExitType;
  addedFilterKeys?: string[];
  addedFilterCountBucket?: '0' | '1-2' | '3-5' | '6+';
  result?: WorklistMutationResultType;
  column?: string;
  columnAction?: WorklistColumnActionType;
  columnsBucket?: '0' | '1-2' | '3-5' | '6-8' | '9-12' | '12+';
  pinDirection?: 'left' | 'right' | 'unpin';
  pinnedCountLeft?: number;
  pinnedCountRight?: number;
  tableVersion?: 'v1' | 'v2';
  startedAt?: number;
} & Record<string, unknown>;

/**
 * These are optional details that is used for measurement, but are NOT sent to analytics.
 * - startingRowsLength: the number of rows present at the start of a load (used to determine if rows were added)
 */
export type MeasureMetadata = {
  startingRowsLength?: number;
} & Record<string, unknown>;

/**
 * This is the shape of an active measurement that is being tracked
 * It is what allows us to track multiple measurements in flight at the same time,
 * and match them up when the end mark is created
 */
export type ActiveMeasurementEntry = MeasureEntry & {
  id: string;
  detail?: MeasureDetail;
  timestamp?: number; // performance.now() when the measurement was started, to enforce TTL
  metadata?: MeasureMetadata;
};

/**
 * Atom family to track the latest active measurement entry by its end mark name
 * This allows multiple measurements to be tracked in parallel, and matched up
 * when the end mark is created.
 *
 * This is required because multiple measurements may share the same end mark name, since
 * actions like searching, filtering, sorting etc all end up triggering the same "worklist load complete" event.
 * We need to be able to differentiate between them to send the correct metrics.
 */
export const latestByEndMarkAtom = atomFamily<ActiveMeasurementEntry | null, string>({
  key: 'worklistAnalyticsLatestByEndMark',
  default: null,
});

/**
 * Atom family to track the last measured entry by its end mark name. This will be used to ensure that
 * certain measurements only take place if a certain previous measurement was done with the same loadID.
 * This is only really relevant for the load metrics like request_fired -> data_ready -> paint.
 */
export const latestMeasuredByEndMarkAtom = atomFamily<ActiveMeasurementEntry | null, string>({
  key: 'latestMeasuredByEndMark',
  default: null,
});
